import { Mail, Phone, MapPin, Send, MessageSquare, Clock } from 'lucide-react';
import { useState } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const contactMethods = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      details: "contact@growon.com",
      description: "For general inquiries and partnerships"
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "WhatsApp Community",
      details: "Join our community group",
      description: "For quick discussions and peer support"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Response Time",
      details: "24-48 hours",
      description: "We typically respond within 1-2 business days"
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    alert('Thank you for your message! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-6 text-white">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Have questions about mentorship, workshops, collaborations, or anything else? We're here to help.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Methods */}
            <div className="space-y-6">
              {contactMethods.map((method, index) => (
                <div key={index} className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mr-4 text-white">
                      {method.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{method.title}</h3>
                      <p className="text-gray-400 text-sm">{method.description}</p>
                    </div>
                  </div>
                  <p className="text-gray-300">{method.details}</p>
                </div>
              ))}
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                <h2 className="text-2xl font-bold mb-8 text-white">Send us a Message</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-300 mb-3 font-medium">Your Name *</label>
                      <input 
                        type="text" 
                        required
                        className="w-full px-5 py-4 bg-gray-900 rounded-xl border border-gray-700 focus:border-blue-500 focus:outline-none text-white placeholder-gray-500"
                        placeholder="Enter your name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-300 mb-3 font-medium">Email Address *</label>
                      <input 
                        type="email" 
                        required
                        className="w-full px-5 py-4 bg-gray-900 rounded-xl border border-gray-700 focus:border-blue-500 focus:outline-none text-white placeholder-gray-500"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-3 font-medium">Subject *</label>
                    <select 
                      required
                      className="w-full px-5 py-4 bg-gray-900 rounded-xl border border-gray-700 focus:border-blue-500 focus:outline-none text-white"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    >
                      <option value="">Select a subject</option>
                      <option value="mentorship">Mentorship Inquiry</option>
                      <option value="workshops">Workshop Registration</option>
                      <option value="collaboration">Business Collaboration</option>
                      <option value="internship">Internship Opportunities</option>
                      <option value="community">Community Questions</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-3 font-medium">Your Message *</label>
                    <textarea 
                      required
                      rows="6"
                      className="w-full px-5 py-4 bg-gray-900 rounded-xl border border-gray-700 focus:border-blue-500 focus:outline-none text-white placeholder-gray-500"
                      placeholder="Tell us how we can help you..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                    />
                  </div>

                  <button 
                    type="submit"
                    className="w-full py-5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center justify-center group"
                  >
                    <Send className="w-5 h-5 mr-3 group-hover:translate-x-1 transition-transform" />
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold mb-8 text-white text-center">Frequently Asked Questions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-800 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-3">How quickly do mentors respond?</h3>
                <p className="text-gray-400">Most mentors respond within 24-48 hours. Sessions are scheduled based on mutual availability.</p>
              </div>
              <div className="bg-gray-800 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-3">Are workshops free?</h3>
                <p className="text-gray-400">We offer both free awareness sessions and paid deep-dive workshops. Check individual workshop details.</p>
              </div>
              <div className="bg-gray-800 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-3">How do I join the community?</h3>
                <p className="text-gray-400">Click the "Join Community" button on the community page to join our WhatsApp/Discord groups.</p>
              </div>
              <div className="bg-gray-800 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-3">Do you guarantee placements?</h3>
                <p className="text-gray-400">No. We focus on skill development and guidance. We don't make false placement promises.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;